'use client';

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Menu, X, LogIn, MessageCircle, Shuffle, Rss, User, LogOut, Users, Home, Zap } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { NavDock } from '@/components/ui/dock';
import { useAuth } from '@/hooks/useAuth';
import { useDMNotifications, useDMStore } from '@/hooks/useDMNotifications';
import { cn } from '@/lib/utils';

// ── Avatar dropdown ─────────────────────────────────────────────────────────
function AvatarDropdown({ user, logout }: { user: { username?: string; email?: string; gender?: string } | null; logout: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const dmTotal = useDMStore((s) => s.total);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const initial = (user?.username?.[0] || user?.email?.[0] || 'U').toUpperCase();

  const menuItems = [
    { label: 'My Profile', href: '/profile', icon: <User size={14} />, badge: 0 },
    { label: 'Friends', href: '/friends', icon: <Users size={14} />, badge: dmTotal },
    { label: 'Feed', href: '/feed', icon: <Rss size={14} />, badge: 0 },
  ];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative w-9 h-9 rounded-full bg-brand-lime/20 border border-brand-lime/30 hover:border-brand-lime/60 hover:bg-brand-lime/30 flex items-center justify-center focus:outline-none active:scale-[0.94]"
        style={{
          transition:
            'border-color 180ms cubic-bezier(0.23, 1, 0.32, 1), background-color 180ms cubic-bezier(0.23, 1, 0.32, 1), transform 120ms ease-out',
        }}
        aria-label="Account menu"
      >
        <span className="text-brand-lime font-bold text-sm">{initial}</span>
        {dmTotal > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-red text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-brand-black">
            {dmTotal > 99 ? '99+' : dmTotal}
          </span>
        )}
      </button>

      <div
        className={cn(
          'absolute top-full right-0 mt-2 w-44 origin-top-right rounded-2xl border border-white/10 bg-brand-surface/95 backdrop-blur-xl shadow-2xl overflow-hidden',
          'transition-all duration-200 ease-out',
          open
            ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 scale-95 -translate-y-1 pointer-events-none'
        )}
        style={{ filter: open ? 'blur(0px)' : 'blur(4px)', transition: 'opacity 0.2s, transform 0.2s, filter 0.2s' }}
      >
        <div className="px-4 py-3 border-b border-white/[0.06]">
          <p className="text-xs font-semibold text-brand-white truncate">
            {user?.username || user?.email?.split('@')[0] || 'Anonymous'}
          </p>
          <p className="text-[11px] text-brand-gray mt-0.5">Member</p>
        </div>

        <div className="p-1.5 flex flex-col gap-0.5">
          {menuItems.map((item, i) => (
            <Link
              key={i}
              href={item.href}
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm text-brand-gray-light hover:text-brand-white hover:bg-brand-surface-2 transition-colors"
            >
              <span className="text-brand-lime">{item.icon}</span>
              {item.label}
            </Link>
          ))}

          <div className="h-px bg-white/[0.06] my-1" />

          <button
            onClick={() => { setOpen(false); logout(); }}
            className="flex items-center gap-2.5 w-full px-3 py-2 rounded-xl text-sm text-brand-gray hover:text-brand-red hover:bg-brand-red/5 transition-colors"
          >
            <LogOut size={14} />
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Mobile Bottom Navigation ────────────────────────────────────────────────
function MobileBottomNav({ isAuthenticated }: { isAuthenticated: boolean }) {
  const pathname = usePathname();

  const items = [
    { icon: Home,          label: 'Home',    href: '/' },
    { icon: MessageCircle, label: 'Chat',    href: '/chat' },
    { icon: Rss,           label: 'Feed',    href: '/feed' },
    { icon: Zap,           label: 'Premium', href: '/subscribe' },
    { icon: User,          label: isAuthenticated ? 'Profile' : 'Sign In', href: isAuthenticated ? '/profile' : '/login' },
  ];

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 md:hidden border-t border-white/[0.06] bg-brand-black/90 backdrop-blur-xl"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      aria-label="Mobile navigation"
    >
      <div className="flex items-center justify-around px-1 pt-2 pb-2">
        {items.map(({ icon: Icon, label, href }) => {
          const basePath = href.split('?')[0];
          const isActive = href === '/' ? pathname === '/' : pathname.startsWith(basePath);
          return (
            <Link
              key={href}
              href={href}
              className="flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl min-w-[56px] min-h-[44px] active:bg-white/[0.06] active:scale-[0.96]"
              style={{
                transition:
                  'background-color 160ms cubic-bezier(0.23, 1, 0.32, 1), transform 120ms ease-out',
              }}
            >
              <Icon
                size={21}
                strokeWidth={isActive ? 2.5 : 1.75}
                className={cn('transition-colors', isActive ? 'text-brand-lime' : 'text-white/40')}
              />
              <span className={cn('text-[10px] font-medium transition-colors', isActive ? 'text-brand-lime' : 'text-white/40')}>
                {label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

// ── Navbar ───────────────────────────────────────────────────────────────────
export function Navbar() {
  const pathname = usePathname();
  const { isAuthenticated, user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Subscribe to DM notifications (unread badge + toast on new message)
  useDMNotifications();

  // Don't show navbar inside the chat layout (it has its own full-screen UI)
  if (pathname === '/chat') return null;

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-40 border-b border-white/[0.06]">
        <div className="absolute inset-0 bg-brand-black/80 backdrop-blur-xl" />

        <nav className="relative max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center shrink-0 z-10">
            <div className="w-9 h-9 rounded-full bg-brand-lime flex items-center justify-center">
              <span className="text-brand-black font-bold text-sm tracking-tight">SW</span>
            </div>
          </Link>

          {/* Desktop Nav — Dock (absolutely centered) */}
          <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 items-center">
            <NavDock isAuthenticated={isAuthenticated} />
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            {isAuthenticated ? (
              <>
                <Link href="/chat">
                  <Button size="sm">
                    <Shuffle size={14} />
                    Stranger Chat
                  </Button>
                </Link>
                <AvatarDropdown user={user} logout={logout} />
              </>
            ) : (
              <>
                <Link href="/login">
                  <Button variant="ghost" size="sm">
                    <LogIn size={14} />
                    Sign In
                  </Button>
                </Link>
                <Link href="/chat">
                  <Button size="sm">
                    <MessageCircle size={14} />
                    Start Chatting
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile: right side — chat CTA + hamburger */}
          <div className="md:hidden flex items-center gap-2">
            <Link href="/chat">
              <Button size="sm" className="h-8 px-3 text-xs">
                <MessageCircle size={13} />
                Chat
              </Button>
            </Link>
            <button
              className="w-9 h-9 flex items-center justify-center rounded-lg text-brand-gray-light hover:text-brand-white transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </nav>

        {/* Mobile drawer menu */}
        {mobileOpen && (
          <div className="relative md:hidden border-t border-white/[0.06] bg-brand-surface animate-slide-up">
            <div className="flex flex-col gap-1 p-4">
              <Link
                href="/"
                className="px-4 py-3 rounded-lg text-sm text-brand-gray-light hover:text-brand-white hover:bg-brand-surface-2"
                onClick={() => setMobileOpen(false)}
              >
                Home
              </Link>
              <Link
                href="/support"
                className="px-4 py-3 rounded-lg text-sm text-brand-gray-light hover:text-brand-white hover:bg-brand-surface-2"
                onClick={() => setMobileOpen(false)}
              >
                Support
              </Link>
              <div className="h-px bg-white/10 my-2" />
              {isAuthenticated ? (
                <>
                  <Link href="/profile" onClick={() => setMobileOpen(false)}>
                    <Button fullWidth variant="secondary" size="md">
                      <User size={14} />
                      My Profile
                    </Button>
                  </Link>
                  <Link href="/friends" onClick={() => setMobileOpen(false)}>
                    <Button fullWidth variant="secondary" size="md">
                      <Users size={14} />
                      Friends
                    </Button>
                  </Link>
                  <button
                    onClick={() => { logout(); setMobileOpen(false); }}
                    className="flex items-center gap-2 px-4 py-3 text-sm text-brand-gray hover:text-brand-red text-left"
                  >
                    <LogOut size={14} />
                    Sign Out
                  </button>
                </>
              ) : (
                <Link href="/login" onClick={() => setMobileOpen(false)}>
                  <Button fullWidth variant="ghost" size="md">Sign In</Button>
                </Link>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Mobile bottom navigation */}
      <MobileBottomNav isAuthenticated={isAuthenticated} />
    </>
  );
}
